import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useNavigate, Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Loader2, Plus, X, Upload, Film, Video, BarChart2, Clock, Settings, Radio, LogOut, Youtube, Zap } from 'lucide-react';
import VideoUpload from '../components/VideoUpload';
import ContentForm from '../components/admin/ContentForm';
import ContentTab from '../components/admin/ContentTab';
import VideosTab from '../components/admin/VideosTab';
import ActivityTab from '../components/admin/ActivityTab';
import SettingsTab from '../components/admin/SettingsTab';
import LiveStreamingTab from '../components/admin/LiveStreamingTab';
import AnalyticsTab from '../components/admin/AnalyticsTab';
import YouTubeTab from '../components/admin/YouTubeTab';
import LiveTickerTab from '../components/admin/LiveTickerTab';

export default function Admin() {
  const navigate = useNavigate();
  const [content, setContent] = useState([]);
  const [uploadedVideos, setUploadedVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  const [auditLog, setAuditLog] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditLoading, setAuditLoading] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  
  // Live Streaming state
  const [liveStreams, setLiveStreams] = useState([]);
  const [showCreateStream, setShowCreateStream] = useState(false);
  const [streamForm, setStreamForm] = useState({
    title: '', teams: '', competition_name: '', is_premium: false, is_test: false, scheduled_start: ''
  });
  const [streamLoading, setStreamLoading] = useState(false);
  const [selectedStream, setSelectedStream] = useState(null);
  
  // Analytics state
  const [analyticsOverview, setAnalyticsOverview] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '', description: '', thumbnail_url: '', hero_image: '', video_url: '',
    content_type: 'vod', category: 'match', competition_name: '',
    teams: '', is_premium: false, is_live: false, start_time: '',
    featured: false, ppv_price: null
  });

  const defaultFormData = {
    title: '', description: '', thumbnail_url: '', hero_image: '', video_url: '',
    content_type: 'vod', category: 'match', competition_name: '',
    teams: '', is_premium: false, is_live: false, start_time: '',
    featured: false, ppv_price: null
  };

  useEffect(() => {
    fetchContent();
    fetchUploadedVideos();
  }, []);

  const fetchOpts = (() => {
    const opts = { credentials: 'include', headers: {} };
    const token = sessionStorage.getItem('admin_token');
    if (token) opts.headers['Authorization'] = `Bearer ${token}`;
    return opts;
  })();

  function adminFetch(url, opts = {}) {
    const token = sessionStorage.getItem('admin_token');
    const headers = { ...(opts.headers || {}) };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return fetch(url, { ...opts, credentials: 'include', headers });
  }

  const fetchContent = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/content/all', fetchOpts);
      if (res.ok) setContent(await res.json());
    } catch (error) {
      toast.error('Failed to fetch content');
    } finally {
      setLoading(false);
    }
  };

  const fetchUploadedVideos = async () => {
    try {
      const res = await fetch('/api/admin/videos', fetchOpts);
      if (res.ok) {
        const data = await res.json();
        setUploadedVideos(data.videos || []);
      }
    } catch (error) {
      console.error('Failed to fetch uploaded videos:', error);
    }
  };

  const fetchAuditLog = async (page = 1) => {
    setAuditLoading(true);
    try {
      const res = await fetch(`/api/admin/audit-log?page=${page}&limit=20`, fetchOpts);
      if (res.ok) {
        const data = await res.json();
        setAuditLog(data.entries || []);
        setAuditTotal(data.total || 0);
        setAuditPage(page);
      }
    } catch { /* silent */ } finally { setAuditLoading(false); }
  };

  const fetchLiveStreams = async () => {
    try {
      const res = await fetch('/api/admin/live-streams', fetchOpts);
      if (res.ok) {
        const data = await res.json();
        setLiveStreams(data.streams || []);
      }
    } catch (error) {
      console.error('Failed to fetch live streams:', error);
    }
  };

  const fetchAnalyticsOverview = async () => {
    try {
      const res = await fetch('/api/admin/analytics/overview', fetchOpts);
      if (res.ok) {
        const data = await res.json();
        setAnalyticsOverview(data);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'activity') fetchAuditLog(1);
    if (activeTab === 'live') fetchLiveStreams();
    if (activeTab === 'analytics') fetchAnalyticsOverview();
  }, [activeTab]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (pwForm.new_password.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    setPwLoading(true);
    try {
      const res = await adminFetch('/api/admin/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: pwForm.current_password, new_password: pwForm.new_password }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Failed to change password');
        return;
      }
      toast.success('Password changed successfully');
      setPwForm({ current_password: '', new_password: '', confirm: '' });
    } catch {
      toast.error('Network error');
    } finally { setPwLoading(false); }
  };

  const handleLogout = async () => {
    await adminFetch('/api/admin/auth/logout', { method: 'POST' });
    sessionStorage.removeItem('admin_token');
    navigate('/admin/login', { replace: true });
  };

  // Live Stream handlers
  const handleCreateStream = async (e) => {
    e.preventDefault();
    setStreamLoading(true);
    try {
      const res = await adminFetch('/api/admin/live-streams/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(streamForm)
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Failed to create live stream');
        return;
      }
      const data = await res.json();
      toast.success(data.scheduled_start ? 'Stream scheduled! Copy RTMP credentials below.' : 'Live stream created! Copy RTMP credentials below.');
      setSelectedStream(data);
      setShowCreateStream(false);
      setStreamForm({ title: '', teams: '', competition_name: '', is_premium: false, is_test: false, scheduled_start: '' });
      fetchLiveStreams();
    } catch (error) {
      toast.error('Failed to create live stream');
    } finally {
      setStreamLoading(false);
    }
  };

  const handleGoLive = async (streamId) => {
    try {
      const res = await adminFetch(`/api/admin/live-streams/${streamId}/go-live`, {
        method: 'POST',
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Cannot publish stream');
        return;
      }
      toast.success('Stream is now LIVE on the platform!');
      fetchLiveStreams();
    } catch {
      toast.error('Failed to publish stream');
    }
  };

  const handleEndStream = async (streamId) => {
    if (!window.confirm('End this live stream? It will be converted to a replay.')) return;
    try {
      const res = await adminFetch(`/api/admin/live-streams/${streamId}/end`, {
        method: 'POST',
      });
      if (res.ok) {
        toast.success('Stream ended. Recording available as replay.');
        fetchLiveStreams();
      }
    } catch {
      toast.error('Failed to end stream');
    }
  };

  const handleDeleteStream = async (streamId) => {
    if (!window.confirm('Delete this stream permanently?')) return;
    try {
      const res = await adminFetch(`/api/admin/live-streams/${streamId}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        toast.success('Stream deleted');
        fetchLiveStreams();
        if (selectedStream?.stream_id === streamId) setSelectedStream(null);
      }
    } catch {
      toast.error('Failed to delete stream');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const handleVideoUploadComplete = () => {
    fetchUploadedVideos();
    fetchContent();
    setShowUploadForm(false);
    toast.success('Video published to content library!');
  };

  const checkVideoStatus = async (videoId) => {
    try {
      await fetch(`/api/admin/videos/${videoId}/status`, fetchOpts);
      fetchUploadedVideos();
    } catch (error) {
      toast.error('Failed to check video status');
    }
  };

  const publishVideo = async (videoId) => {
    try {
      const res = await adminFetch(`/api/admin/videos/${videoId}/publish`, {
        method: 'POST',
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Failed');
      }
      toast.success('Video published successfully!');
      fetchUploadedVideos();
      fetchContent();
    } catch (error) {
      toast.error(error.message || 'Failed to publish video');
    }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    try {
      await adminFetch(`/api/admin/videos/${videoId}`, {
        method: 'DELETE',
      });
      toast.success('Video deleted');
      fetchUploadedVideos();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (payload.ppv_price) payload.ppv_price = parseFloat(payload.ppv_price);
      const url = editingId ? `/api/admin/content/${editingId}` : '/api/admin/content';
      const res = await adminFetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Request failed');
      toast.success(editingId ? 'Content updated successfully' : 'Content created successfully');
      setFormData(defaultFormData);
      setEditingId(null);
      setShowAddForm(false);
      fetchContent();
    } catch (error) {
      toast.error(editingId ? 'Failed to update content' : 'Failed to create content');
    }
  };

  const handleEdit = (item) => {
    setFormData({
      title: item.title, description: item.description,
      thumbnail_url: item.thumbnail_url, hero_image: item.hero_image || '',
      video_url: item.video_url, content_type: item.content_type,
      category: item.category, competition_name: item.competition_name || '',
      teams: item.teams || '', is_premium: item.is_premium,
      is_live: item.is_live, start_time: item.start_time || '',
      featured: item.featured || false, ppv_price: item.ppv_price || null
    });
    setEditingId(item.id);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    try {
      await adminFetch(`/api/admin/content/${id}`, {
        method: 'DELETE',
      });
      toast.success('Content deleted successfully');
      fetchContent();
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  const handleCancel = () => {
    setFormData(defaultFormData);
    setEditingId(null);
    setShowAddForm(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a1a]">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-24">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white">Content Management</h1>
          </div>
          <div className="flex gap-3">
            <Link
              to="/admin/metrics"
              data-testid="admin-metrics-link"
              className="flex items-center gap-2 px-4 py-2 bg-[#222] hover:bg-[#2a2a2a] border border-white/10 text-white/70 hover:text-white rounded-lg transition-colors text-sm font-medium"
            >
              <BarChart2 className="w-4 h-4" />
              Metrics
            </Link>
            {activeTab === 'content' || activeTab === 'videos' ? (
              <>
                <button
                  onClick={() => { setShowUploadForm(!showUploadForm); setShowAddForm(false); }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  data-testid="upload-video-btn"
                >
                  <Upload className="w-5 h-5" />
                  Upload Video
                </button>
                <button
                  onClick={() => { setShowAddForm(!showAddForm); setShowUploadForm(false); }}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
                  data-testid="add-content-btn"
                >
                  {showAddForm ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                  {showAddForm ? 'Cancel' : 'Add URL Content'}
                </button>
              </>
            ) : null}
            {activeTab === 'live' && (
              <button
                onClick={() => setShowCreateStream(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                data-testid="new-live-stream-btn"
              >
                <Radio className="w-5 h-5" />
                New Live Stream
              </button>
            )}
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 px-4 py-2 bg-[#222] hover:bg-red-900/40 border border-white/10 text-white/50 hover:text-red-400 rounded-lg transition-colors text-sm"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-gray-700 overflow-x-auto">
          {[
            { key: 'content', label: `Published Content (${content.length})`, icon: Film },
            { key: 'videos', label: `Uploaded Videos (${uploadedVideos.length})`, icon: Video },
            { key: 'youtube', label: 'YouTube', icon: Youtube, isYT: true },
            { key: 'ticker', label: 'Live Ticker', icon: Zap, isTicker: true },
            { key: 'activity', label: 'Activity Log', icon: Clock },
            { key: 'settings', label: 'Settings', icon: Settings },
            { key: 'live', label: 'Live Streaming', icon: Radio, isLive: true },
            { key: 'analytics', label: 'Analytics', icon: BarChart2 },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              data-testid={`tab-${tab.key}`}
              className={`pb-3 px-1 font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.key
                  ? tab.isLive || tab.isTicker ? 'text-red-500 border-b-2 border-red-500'
                  : tab.isYT ? 'text-red-500 border-b-2 border-red-500'
                  : 'text-emerald-500 border-b-2 border-emerald-500'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4 inline mr-2" />
              {tab.label}
              {tab.isLive && liveStreams.some(s => s.is_live) && (
                <span className="ml-2 w-2 h-2 bg-red-500 rounded-full inline-block animate-pulse" />
              )}
            </button>
          ))}
        </div>

        {/* Video Upload Form */}
        {showUploadForm && (
          <VideoUpload 
            onUploadComplete={handleVideoUploadComplete}
            onClose={() => setShowUploadForm(false)}
          />
        )}

        {/* Add/Edit Content Form */}
        {showAddForm && (
          <ContentForm
            formData={formData}
            handleInputChange={handleInputChange}
            handleSubmit={handleSubmit}
            handleCancel={handleCancel}
            editingId={editingId}
          />
        )}

        {/* Tab Content */}
        {activeTab === 'content' && (
          <ContentTab content={content} handleEdit={handleEdit} handleDelete={handleDelete} />
        )}

        {activeTab === 'videos' && (
          <VideosTab
            uploadedVideos={uploadedVideos}
            checkVideoStatus={checkVideoStatus}
            publishVideo={publishVideo}
            deleteVideo={deleteVideo}
          />
        )}

        {activeTab === 'activity' && (
          <ActivityTab
            auditLog={auditLog}
            auditLoading={auditLoading}
            auditTotal={auditTotal}
            auditPage={auditPage}
            fetchAuditLog={fetchAuditLog}
          />
        )}

        {activeTab === 'settings' && (
          <SettingsTab
            pwForm={pwForm}
            setPwForm={setPwForm}
            pwLoading={pwLoading}
            handleChangePassword={handleChangePassword}
          />
        )}

        {activeTab === 'live' && (
          <LiveStreamingTab
            liveStreams={liveStreams}
            showCreateStream={showCreateStream}
            setShowCreateStream={setShowCreateStream}
            streamForm={streamForm}
            setStreamForm={setStreamForm}
            streamLoading={streamLoading}
            selectedStream={selectedStream}
            setSelectedStream={setSelectedStream}
            handleCreateStream={handleCreateStream}
            handleGoLive={handleGoLive}
            handleEndStream={handleEndStream}
            handleDeleteStream={handleDeleteStream}
            copyToClipboard={copyToClipboard}
          />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsTab analyticsOverview={analyticsOverview} />
        )}

        {activeTab === 'youtube' && (
          <YouTubeTab />
        )}

        {activeTab === 'ticker' && (
          <LiveTickerTab />
        )}
      </div>
    </div>
  );
}
